import { parsePhoneNumberFromString } from 'libphonenumber-js'
import type { Phone } from '@consumer/types/Phone'

export type { Phone }

interface FormatPhoneNumberOptions {
  includeCountryCode?: boolean
}

/**
 * Formats a phone number string for display.
 * @param value - Phone number string (E.164 or national format)
 * @param options.includeCountryCode - Whether to include +1 prefix for US numbers
 * @returns Formatted string like "(555) 123-4567" or "+1 (555) 123-4567"
 */
export function formatPhoneNumber (value?: string | null, options: FormatPhoneNumberOptions = {}) {
  const { includeCountryCode = false } = options

  if (!value) return ''

  // match (n**) ***-**** for masked numbers, skip formatting
  if (value.match(/^\(\d\*{2}\) \*{3}-\*{4}$/)) {
    return value
  }

  // Try to parse as E.164 or with US as default country
  const parsed = parsePhoneNumberFromString(value, 'US')
  if (parsed) {
    if (includeCountryCode) {
      return parsed.formatInternational()
    }
    return parsed.formatNational()
  }

  // Fallback to manual formatting for US numbers
  const cleaned = value.replace(/\D/g, '')

  // US number with country code (11 digits starting with 1)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const national = cleaned.slice(1)
    const formatted = `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
    return includeCountryCode ? `+1 ${formatted}` : formatted
  }

  // US number without country code (10 digits)
  if (cleaned.length === 10) {
    const formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    return includeCountryCode ? `+1 ${formatted}` : formatted
  }

  // Return original value for international numbers (preserve + prefix)
  return value
}

/**
 * Formats an international phone number for display.
 * @param countryCode - Country dialing code (e.g., "1" for US, "44" for UK)
 * @param value - National phone number digits
 * @returns Formatted string like "+1 (555) 123-4567" or "+44123456789"
 */
export function formatInternationalPhoneNumber (countryCode: string | undefined, value?: string | undefined) {
  if (countryCode === '1' || !countryCode) {
    return formatPhoneNumber(value, { includeCountryCode: true })
  }
  else {
    return `+${countryCode}${value}`
  }
}

/**
 * Parses an E.164 string into a Phone object for form editing.
 * @param value - E.164 string like "+15551234567", or existing Phone object
 * @returns Phone object with countryIsoCode, countryDialingCode, and formatted number
 */
export function parsePhone (value?: string | Phone | null): Phone {
  if (!value) {
    return { countryIsoCode: 'US', countryDialingCode: '1', number: '' }
  }

  // If already a Phone object, return a copy
  if (typeof value === 'object') {
    return {
      countryIsoCode: value.countryIsoCode || 'US',
      countryDialingCode: value.countryDialingCode || '1',
      number: value.number || '',
    }
  }

  // Try to parse using libphonenumber-js
  const parsed = parsePhoneNumberFromString(value, 'US')
  if (parsed) {
    return {
      countryIsoCode: parsed.country || 'US',
      countryDialingCode: parsed.countryCallingCode || '1',
      number: parsed.formatNational(),
    }
  }

  // Fallback: manual parsing for edge cases
  const cleaned = value.replace(/\D/g, '')

  // US/Canada number (+1)
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    const nationalNumber = cleaned.slice(1)
    return {
      countryIsoCode: 'US',
      countryDialingCode: '1',
      number: `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`,
    }
  }

  // 10-digit US number without country code
  if (cleaned.length === 10) {
    return {
      countryIsoCode: 'US',
      countryDialingCode: '1',
      number: `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`,
    }
  }

  // Default: treat as US with raw value
  return { countryIsoCode: 'US', countryDialingCode: '1', number: value }
}

/**
 * Converts a Phone object to E.164 format for API submission.
 * @param phone - Phone object from GPhoneInput
 * @returns E.164 formatted string like "+15551234567", or null if empty
 */
export function phoneToE164 (phone?: Phone | null): string | null {
  if (!phone?.number) return null
  const digits = phone.number.replace(/\D/g, '')
  if (!digits) return null

  // Use libphonenumber-js for proper E.164 formatting
  const parsed = parsePhoneNumberFromString(phone.number, phone.countryIsoCode as any)
  if (parsed) {
    return parsed.format('E.164')
  }

  // Fallback: manually construct E.164 with + prefix
  return `+${phone.countryDialingCode || '1'}${digits}`
}
